/**
 * Top-level app dispatcher — role-aware split between the three
 * domain shells (Rider / Driver / Admin).
 *
 * Architecture:
 *   /apps/rider/RiderApp.jsx   ← booking, dashboard, trips, account
 *   /apps/driver/DriverApp.jsx ← driver execution UI only
 *   /apps/admin/AdminApp.jsx   ← operator dashboards + ops tools
 *   /shared/components|context|lib|hooks ← code reused across shells
 *
 * Dispatch rule (in this order):
 *
 *   1. Path starts with `/admin`   → AdminApp.
 *   2. Path starts with `/driver`  → DriverApp.
 *   3. Auth resolved + user.is_driver === true → DriverApp.
 *   4. Auth resolved + user.role === "admin"  → AdminApp.
 *   5. Anything else (loading, anonymous, regular rider) → RiderApp.
 *
 * IMPORTANT — anti-strand rule: during the brief /auth/me loading
 * window we ALWAYS render RiderApp (the public-facing default).
 * Previously we honored a `localStorage.link.role` hint so a driver
 * refreshing on `/` wouldn't see the rider home flash, but that hint
 * can become stale (driver demo'd on a shared device, build mismatch
 * between localStorage and bundle, etc.) and would whitescreen the
 * rider. A ~200ms flash of rider UI for a returning driver on cold
 * refresh is *vastly* better than a stranded rider — and the
 * DriverRouteGuard inside DriverApp will still redirect them to
 * /driver once auth resolves.
 *
 * The DriverApp/AdminApp shells also redirect to /login if the user
 * lands there without permission, so the path-prefix branches are
 * safe to render anonymously too.
 */
import React from "react";
import "@/App.css";
import { BrowserRouter, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";

import { AuthProvider, useAuth } from "@/shared/context/AuthContext";
import { ThemeProvider } from "@/shared/context/ThemeContext";
import { Toaster } from "@/shared/components/ui/sonner";

import RiderApp from "@/apps/rider/RiderApp";
import DriverApp from "@/apps/driver/DriverApp";
import AdminApp from "@/apps/admin/AdminApp";
import QaBadgeAndPanel from "@/shared/components/QaBadgeAndPanel";
import OfflineBanner from "@/shared/components/OfflineBanner";

function Dispatcher() {
  const { pathname } = useLocation();
  const { user, loading } = useAuth();

  // Path-prefix shortcuts ALWAYS win — an anonymous visit to /admin or
  // /driver renders that shell so its own ProtectedRoute can take over.
  if (pathname.startsWith("/admin"))  return <AdminApp />;
  if (pathname.startsWith("/driver")) return <DriverApp />;

  // Active drivers MUST stay inside DriverApp (their workflow is
  // execution-only; they shouldn't be booking rides as a rider).
  // DriverRouteGuard then redirects them to /driver from any non-driver
  // path inside that shell.
  if (!loading && user?.is_driver) return <DriverApp />;

  // Admins (and everyone else) get the rider experience by default —
  // admins explicitly navigate to /admin/* when they need ops tools.
  // This avoids the "admin lands on / → AdminApp has no `/` route →
  // white screen" stranding bug.
  return <RiderApp />;
}

function App() {
  return (
    <div className="App min-h-[100dvh] bg-[#F9F7F4] text-[#0A1128]">
      <BrowserRouter>
        <ThemeProvider>
          {/*
            Global reduced-motion honour. `reducedMotion="user"` tells
            framer-motion to silently disable transforms and animation
            durations whenever the OS-level `prefers-reduced-motion`
            media query matches. Individual crossfade fallbacks are
            still honoured — framer just short-circuits transform-heavy
            morphs, springs, and slides. Applied at the ROOT so every
            drawer, sheet, morph, and page transition inherits it.
          */}
          <MotionConfig reducedMotion="user">
            <AuthProvider>
              <Dispatcher />
              {/* QA badge + optional dev panel — no-op for real riders. */}
              <QaBadgeAndPanel />
              <OfflineBanner />
              <Toaster position="top-center" />
            </AuthProvider>
          </MotionConfig>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
